import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { MANAGER_EFFICIENCIES, MAX_MANAGER_LEVEL, STATIONS } from '../game/content';
import { EstateRenderer } from '../game/EstateRenderer';
import type { GameStore } from '../game/GameStore';
import { audioManager } from '../audio/AudioManager';
import {
  capacityCost,
  managerCost,
  pipelineStatus,
  purchaseQuote,
  stationCapacity,
  stationRate
} from '../game/simulation';
import type { GameState, OfflineSummary, PanelId, StationId } from '../game/types';
import { saveGame } from '../game/persistence';
import { t } from '../i18n/en';
import { ManagementModal, PrestigeModal } from './Panels';
import { PipelineDock } from './PipelineDock';

interface GameShellProps {
  store: GameStore;
  offlineSummary: OfflineSummary | null;
  updateAvailable: boolean;
  onUpdate: () => void;
  onExit: () => void;
}

const TUTORIAL = [
  ['Harvest the first row', 'Select Vineyard and use the owner action.'],
  ['Crush those grapes', 'The crush pad turns grapes into juice.'],
  ['Tend the ferment', 'Punch down in the cellar for wine and quality.'],
  ['Bottle the first lot', 'Cork and label the finished wine.'],
  ['Pour the first flight', 'Sell a case at the tasting patio.'],
  ['Upgrade the bottleneck', 'Spend cash where the chain is slowest.'],
  ['Build a self-running winery', 'Hire every station manager, then train them to improve automatic speed.']
] as const;

export function GameShell({ store, offlineSummary: initialOfflineSummary, updateAvailable, onUpdate, onExit }: GameShellProps) {
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  const [selectedStation, setSelectedStation] = useState<StationId>('vineyard');
  const [panel, setPanel] = useState<PanelId>(null);
  const [buyMode, setBuyMode] = useState<1 | 10 | 25 | 'max' | 'milestone'>(1);
  const [prestigeOpen, setPrestigeOpen] = useState(false);
  const [offlineSummary, setOfflineSummary] = useState(initialOfflineSummary);
  const [toast, setToast] = useState<string | null>(null);
  const phaserRef = useRef<HTMLDivElement>(null);
  const toastTimerRef = useRef<number | null>(null);

  const selectStation = useCallback((stationId: StationId) => setSelectedStation(stationId), []);

  useEffect(() => {
    if (!phaserRef.current) return;
    const renderer = new EstateRenderer(phaserRef.current, store, selectStation);
    return () => renderer.destroy();
  }, [selectStation, store]);

  useEffect(() => {
    store.start();
    let saveTimer: number | null = null;
    const unsubscribe = store.subscribe((next, command) => {
      audioManager.update(next.settings);
      const critical = command && ['buyLevels', 'buyCapacity', 'upgradeManager', 'buyLegacy', 'startVintage', 'setSettings'].includes(command.type);
      if (critical) void saveGame(next);
      else {
        if (saveTimer !== null) window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => void saveGame(store.getState()), 30_000);
      }
    });
    const saveWhenHidden = () => { if (document.visibilityState === 'hidden') void saveGame(store.getState()); };
    document.addEventListener('visibilitychange', saveWhenHidden);
    return () => {
      if (saveTimer !== null) window.clearTimeout(saveTimer);
      if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
      document.removeEventListener('visibilitychange', saveWhenHidden);
      unsubscribe();
      store.stop();
      void saveGame(store.getState());
    };
  }, [store]);

  const station = state.stations[selectedStation];
  const def = STATIONS[selectedStation];
  const quote = purchaseQuote(state, selectedStation, buyMode);
  const outputStage = def.output;
  const stored = outputStage === 'cash'
    ? Object.values(state.inventory).reduce((sum, inventory) => sum + inventory.cases, 0)
    : Object.values(state.inventory).reduce((sum, inventory) => sum + inventory[outputStage], 0);
  const capacity = selectedStation === 'sales' ? Number.POSITIVE_INFINITY : stationCapacity(state, selectedStation);
  const nextCapacity = selectedStation === 'sales' ? Number.POSITIVE_INFINITY : previewCapacity(state, selectedStation);
  const selectedStatus = pipelineStatus(state).find((report) => report.stationId === selectedStation);
  const currentRate = stationRate(state, selectedStation);
  const upgradedRate = previewRate(state, selectedStation, quote.levels);
  const currentManagerEfficiency = MANAGER_EFFICIENCIES[Math.min(MAX_MANAGER_LEVEL, station.managerLevel)];
  const nextManagerEfficiency = MANAGER_EFFICIENCIES[Math.min(MAX_MANAGER_LEVEL, station.managerLevel + 1)];
  const currentManagerCost = managerCost(state, selectedStation);
  const managerAtMax = station.managerLevel >= MAX_MANAGER_LEVEL;
  const temperature = state.settings.temperatureUnit === 'fahrenheit'
    ? Math.round(state.weatherTemperature * 9 / 5 + 32)
    : state.weatherTemperature;
  const temperatureSymbol = state.settings.temperatureUnit === 'fahrenheit' ? '°F' : '°C';
  const tutorial = TUTORIAL[Math.min(state.tutorialStep, TUTORIAL.length - 1)];

  const runAction = useCallback(async () => {
    const currentState = store.getState();
    await audioManager.unlock(currentState.settings);
    store.dispatch({ type: 'manualAction', stationId: selectedStation });
    audioManager.action(selectedStation);
    setToast(currentState.stations[selectedStation].managerLevel > 0 ? `${def.label} overdrive: 2× for 45 seconds.` : def.actionDescription);
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null;
      setToast(null);
    }, 2400);
  }, [def.actionDescription, def.label, selectedStation, store]);

  useEffect(() => {
    const handleKeyboard = (event: KeyboardEvent) => {
      const element = event.target as HTMLElement | null;
      const typing = element?.matches('input, select, textarea, button, [contenteditable="true"]');
      if (event.code === 'Space' && !typing && !panel && !prestigeOpen && !offlineSummary) {
        event.preventDefault();
        void runAction();
      }
      if (event.key === 'Escape') {
        if (prestigeOpen) setPrestigeOpen(false);
        else if (panel) setPanel(null);
        else if (offlineSummary) setOfflineSummary(null);
      }
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [offlineSummary, panel, prestigeOpen, runAction]);

  const importReplacement = (next: GameState) => {
    store.replaceState(next);
    setPanel(null);
  };

  return <main className={`game-shell ${state.settings.highContrast ? 'high-contrast' : ''}`} onPointerDown={() => void audioManager.unlock(state.settings)}>
    <header className="top-hud">
      <div className="hud-brand"><span className="mini-grapes">●●<br /> ●</span><div><b>CRUSH SEASON</b><small>{state.owner.name} · {state.region === 'napa' ? 'Napa Valley' : 'Sonoma County'}</small></div></div>
      <div className="hud-resources"><span><i>¤</i><small>CASH</small><b>${state.cash.format(state.settings.numberFormat)}</b></span><span><i>✦</i><small>RENOWN</small><b>{Math.floor(state.renown).toLocaleString()}</b></span><span><i>✹</i><small>LEGACY</small><b>{state.legacyCorks}</b></span><span><i>☀</i><small>{state.weather.toUpperCase()}</small><b>{temperature}{temperatureSymbol}</b></span></div>
      <div className="hud-actions"><button className="hud-year" onClick={() => setPanel('legacy')}><small>VINTAGE</small><b>{state.vintage} · {state.year}</b></button><button className="icon-button" aria-label="Settings" onClick={() => setPanel('settings')}>⚙</button></div>
    </header>
    <section className="game-stage" aria-label="Winery estate">
      <div ref={phaserRef} className="phaser-host" />
      <aside className="objective-card"><p className="eyebrow">CURRENT OBJECTIVE</p><h2>{tutorial[0]}</h2><p>{tutorial[1]}</p><div className="objective-progress"><i style={{ width: `${Math.min(100, state.tutorialStep / 6 * 100)}%` }} /></div></aside>
      <aside className="station-detail" aria-labelledby="station-title">
        <div className="station-heading"><span className="station-icon">{def.icon}</span><div><p className="eyebrow">SELECTED STATION</p><h2 id="station-title">{def.label}</h2></div><span className={`status-chip status-${selectedStatus?.kind ?? 'manual'}`}>{selectedStatus?.label ?? 'Owner operated'}</span></div>
        <div className="station-metrics"><span><small>LEVEL</small><b>{station.level}</b></span><span><small>THROUGHPUT</small><b>{currentRate.toFixed(1)}/s</b></span><span><small>{def.output === 'cash' ? 'READY CASES' : 'OUTPUT BUFFER'}</small><b>{stored.toFixed(1)}{Number.isFinite(capacity) ? `/${capacity.toFixed(0)}` : ''}</b></span></div>
        <p className={`station-note status-${selectedStatus?.kind ?? 'manual'}`}>{selectedStatus?.explanation}</p>
        <button className="button action-button" onClick={runAction}><span>{def.action}</span><kbd>SPACE</kbd></button>
        <div className="buy-controls"><div className="segmented">{([1, 10, 25, 'max', 'milestone'] as const).map((amount) => <button key={amount} className={buyMode === amount ? 'active' : ''} onClick={() => setBuyMode(amount)}>{amount === 'milestone' ? 'NEXT ★' : amount === 'max' ? 'MAX' : `×${amount}`}</button>)}</div><button className="button upgrade-button" disabled={quote.levels === 0 || !state.cash.gte(quote.cost)} onClick={() => store.dispatch({ type: 'buyLevels', stationId: selectedStation, amount: buyMode })}><span>Upgrade throughput {quote.levels > 1 ? `+${quote.levels}` : ''}</span><b>${quote.cost.format(state.settings.numberFormat)}</b></button></div>
        <p className="upgrade-explanation"><b>{currentRate.toFixed(1)}/s → {upgradedRate.toFixed(1)}/s</b> Processes material faster; does not add buffer space.</p>
        <div className="station-secondary">
          <button disabled={managerAtMax || !state.cash.gte(currentManagerCost)} onClick={() => store.dispatch({ type: 'upgradeManager', stationId: selectedStation })}>
            <span>⚙</span><small>{station.managerLevel === 0 ? 'Hire manager' : `Train manager · L${station.managerLevel}`}</small><b>{managerAtMax ? 'MAX · 100%' : `${Math.round(currentManagerEfficiency * 100)}% → ${Math.round(nextManagerEfficiency * 100)}% · $${currentManagerCost.format(state.settings.numberFormat)}`}</b><em>Automates the station; training increases its speed.</em>
          </button>
          <button disabled={!state.cash.gte(capacityCost(state, selectedStation)) || selectedStation === 'sales'} onClick={() => store.dispatch({ type: 'buyCapacity', stationId: selectedStation })}>
            <span>▣</span><small>Expand output buffer</small><b>{selectedStation === 'sales' ? 'NO BUFFER' : `${capacity.toFixed(0)} → ${nextCapacity.toFixed(0)} · $${capacityCost(state, selectedStation).format(state.settings.numberFormat)}`}</b><em>Holds more output; does not process faster.</em>
          </button>
        </div>
      </aside>
      <PipelineDock state={state} selectedStation={selectedStation} onSelect={selectStation} />
      <nav className="management-dock" aria-label="Winery management">{([{ id: 'wines', icon: '♜', label: 'Programs', title: 'Choose wine priorities and see how shared production is divided.' }, { id: 'sales', icon: '↗', label: 'Pricing', title: 'Set bottle prices and sales-channel allocations.' }, { id: 'estate', icon: '⌂', label: 'Philosophy', title: 'Choose an estate philosophy and review its exact tradeoffs.' }, { id: 'cellar', icon: '★', label: 'History', title: 'Review the permanent bottle history from completed vintages.' }, { id: 'legacy', icon: '✹', label: 'Legacy', title: 'Preview the vintage reset and spend permanent Legacy Corks.' }] as const).map((item) => <button key={item.id} title={item.title} onClick={() => setPanel(item.id)}><span>{item.icon}</span><small>{item.label}</small></button>)}</nav>
      {toast && <div className="toast" role="status">{toast}</div>}
      {updateAvailable && <button className="update-toast" onClick={onUpdate}>{t('updateReady')} <b>Update</b></button>}
    </section>
    {panel && <ManagementModal panel={panel} state={state} store={store} onClose={() => setPanel(null)} onImported={importReplacement} onPrestige={() => setPrestigeOpen(true)} />}
    {prestigeOpen && <PrestigeModal state={state} store={store} onClose={() => setPrestigeOpen(false)} />}
    {offlineSummary && offlineSummary.seconds > 10 && <div className="modal-backdrop"><section className="modal offline-modal" role="dialog" aria-modal="true" aria-labelledby="offline-title"><span className="sunrise-icon">☀</span><p className="eyebrow">WELCOME BACK</p><h2 id="offline-title">The managers kept things moving</h2><p>Your estate ran for {formatDuration(offlineSummary.seconds)} while you were away.</p><div className="score-grid"><span><small>Cash earned</small><b>${offlineSummary.cashEarned.format(state.settings.numberFormat)}</b></span><span><small>Grapes waiting</small><b>{offlineSummary.grapesHarvested.toFixed(1)}</b></span><span><small>Cases sold</small><b>{offlineSummary.casesSold.toFixed(1)}</b></span></div>{offlineSummary.capped && <p className="warning">Your current manager cap limited this report. Legacy upgrades can extend it.</p>}<button className="button button-primary" onClick={() => setOfflineSummary(null)}>Walk the estate</button></section></div>}
    <button className="exit-button" onClick={onExit} title="Save and return to title">Save & Exit</button>
  </main>;
}

function previewRate(state: GameState, stationId: StationId, levels: number): number {
  if (levels <= 0) return stationRate(state, stationId);
  const preview = {
    ...state,
    stations: {
      ...state.stations,
      [stationId]: { ...state.stations[stationId], level: state.stations[stationId].level + levels }
    }
  };
  return stationRate(preview, stationId);
}

function previewCapacity(state: GameState, stationId: StationId): number {
  const preview = {
    ...state,
    stations: {
      ...state.stations,
      [stationId]: { ...state.stations[stationId], capacityLevel: state.stations[stationId].capacityLevel + 1 }
    }
  };
  return stationCapacity(preview, stationId);
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)} minute${minutes === 1 ? '' : 's'}`;
}
